// /src/components/Modals/AddUserModal.tsx

// What? A modal for adding a new user to the application.
// Why? To allow users to add new users to the application.
// How?
// - Uses a Modal component to display the modal.
// - Uses a ModalDialog component to display the modal content.
// - Uses a FormControl component to display the form.
// - Uses a FormLabel component to display the label for the email input.
// - Uses an Input component to display the email input.

import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  DialogTitle,
  Stack,
  Input,
  Button,
  Select,
  Option,
} from "@mui/joy";

// How to instantiate:
// <AddUserModal
//   open={open}
//   handleAddUser={handleAddUser}
//   onClose={onClose}
//   buttonDisabled={buttonDisabled}
//   buttonLabel={buttonLabel}
// />
interface AddUserModalProps {
  open: boolean;
  handleAddUser: (event: React.FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  buttonDisabled: boolean;
  buttonLabel: React.ReactNode;
}

// What? A modal for adding a new user to the application.
// Why? To allow users to add new users to the application.
// How?
// This component implements a modal for adding a new user by:
// 1. Rendering a modal dialog when the 'open' prop is true
// 2. Displaying a form within the modal with fields for email and role
// 3. Managing form state (email and role) using React hooks
// 4. Handling form submission and passing data to the parent component
// 5. Providing options to submit the form or cancel the operation
// 6. Resetting the form state when the modal is closed
//
// The component uses various MUI Joy components to create a consistent UI,
// including Modal, ModalDialog, FormControl, Input, and Select.
const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  handleAddUser,
  onClose,
  buttonDisabled,
  buttonLabel,
}) => {
  const [addUserOpen, setAddUserOpen] = useState<boolean>(open);
  const [role, setRole] = useState<string>("user");
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    setAddUserOpen(open);
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setRole("user");
    setEmail("");
  };

  const handleRoleChange = (value: string) => {
    setRole(value);
  };

  return (
    <Modal open={addUserOpen} onClose={onClose}>
      <ModalDialog
        size="md"
        variant="outlined"
        sx={{
          maxWidth: 400,
          width: "100%",
        }}
      >
        <DialogTitle>Add New User</DialogTitle>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleAddUser(event);
          }}
        >
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input
                name="userEmail"
                autoFocus
                required
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="md"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Role</FormLabel>
              <Select
                value={role}
                onChange={(event, newValue) =>
                  handleRoleChange(newValue as string)
                }
                placeholder="Select role"
                size="md"
              >
                <Option value="admin">Admin</Option>
                <Option value="editor">Editor</Option>
                <Option value="user">User</Option>
              </Select>
            </FormControl>

            {/* Hidden input to ensure role is passed in form submission */}
            <input type="hidden" name="role" value={role} />

            {/* Button container with proper spacing */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ pt: 1 }}
            >
              <Button
                variant="outlined"
                color="neutral"
                onClick={onClose}
                disabled={buttonDisabled}
                size="md"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                type="submit"
                disabled={buttonDisabled}
                size="md"
              >
                {buttonLabel}
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default AddUserModal;
